
import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { useGetHarvestsByFarmId, useGetHarvests, useDeleteHarvest } from '@/hooks/use-harvest';
import { Loader2, PlusCircle, CalendarDays, Upload } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import HarvestListItem from './HarvestListItem';
import EmptyHarvestState from './EmptyHarvestState';
import DeleteHarvestDialog from './DeleteHarvestDialog';

interface HarvestListProps {
  farmId?: string;
}

const HarvestList: React.FC<HarvestListProps> = ({ farmId }) => {
  const navigate = useNavigate();
  
  // Use different hooks based on whether farmId is provided
  const farmHarvestsQuery = useGetHarvestsByFarmId(farmId || '');
  const allHarvestsQuery = useGetHarvests();
  
  // Choose the appropriate query based on farmId presence
  const { data: harvests, isLoading, error } = farmId ? farmHarvestsQuery : allHarvestsQuery;
  
  const deleteHarvest = useDeleteHarvest();
  const [harvestToDelete, setHarvestToDelete] = React.useState<string | null>(null);

  const handleDelete = async () => {
    if (!harvestToDelete) return;
    
    try {
      await deleteHarvest.mutateAsync(harvestToDelete);
      toast({
        title: "Harvest deleted",
        description: "The harvest has been successfully deleted.",
      });
    } catch (error) {
      console.error('Error deleting harvest:', error);
      toast({
        title: "Error",
        description: "Failed to delete harvest. Please try again.",
        variant: "destructive"
      });
    } finally {
      setHarvestToDelete(null);
    }
  };

  const handleAddHarvest = () => {
    if (farmId) {
      navigate(`/harvests/new?farmId=${farmId}`);
    } else {
      navigate('/harvests/new');
    }
  };

  if (isLoading) {
    return <div className="flex justify-center p-6"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-800 rounded-md">
        Error loading harvests: {error.message}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div>
          <CardTitle className="text-xl">
            {farmId ? 'Harvests' : 'All Harvests'}
          </CardTitle>
          <CardDescription>
            {farmId ? 'Manage harvest records for this farm' : 'Manage all harvest records'}
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          {!farmId && (
            <>
              <Button variant="outline" size="sm" asChild>
                <Link to="/harvest-calendar">
                  <CalendarDays className="mr-2 h-4 w-4" />
                  Calendar View
                </Link>
              </Button>
              <Button variant="outline" size="sm" asChild>
                <Link to="/farms/upload">
                  <Upload className="mr-2 h-4 w-4" />
                  Upload Farms
                </Link>
              </Button>
            </>
          )}
          <Button 
            onClick={handleAddHarvest}
            size="sm"
          >
            <PlusCircle className="mr-2 h-4 w-4" />
            Add Harvest
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {harvests && harvests.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Status</TableHead>
                <TableHead>Product</TableHead>
                {!farmId && <TableHead>Farm</TableHead>}
                <TableHead>Expected Date</TableHead>
                <TableHead>Expected Quantity</TableHead>
                <TableHead className="hidden md:table-cell">Quality</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {harvests.map((harvest) => (
                <HarvestListItem 
                  key={harvest.id} 
                  harvest={harvest} 
                  onDeleteClick={setHarvestToDelete}
                  showFarmInfo={!farmId}
                />
              ))}
            </TableBody>
          </Table>
        ) : (
          <EmptyHarvestState farmId={farmId} />
        )}
      </CardContent>

      <DeleteHarvestDialog
        open={!!harvestToDelete}
        onOpenChange={(open) => !open && setHarvestToDelete(null)}
        onConfirm={handleDelete}
        isPending={deleteHarvest.isPending}
      />
    </Card>
  );
};

export default HarvestList;
